import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        setAuthenticated(true);
        fetchStats(password);
      }
    } catch (err) {
      setError(err.response?.status === 429 ? 'Too many attempts. Try again later.' : 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (adminKey) => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { headers: { 'X-Admin-Key': adminKey } });
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md px-6"
        >
          <h1 className="text-3xl font-bold tracking-tight text-[#0A0A0B] mb-8 text-center" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Admin Login
          </h1>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block mb-3 text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">
                Password
              </label>
              <input
                data-testid="admin-login-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full bg-white border border-[#E5E5EB] rounded-xl px-6 py-4 text-lg font-medium text-[#0A0A0B] focus:outline-none focus:ring-2 focus:ring-[#534AB7] focus:border-transparent transition-all"
              />
            </div>

            {error && (
              <p className="text-[#EF4444] text-sm">{error}</p>
            )}

            <button
              data-testid="admin-login-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-[#534AB7] text-white hover:bg-[#423B99] hover:-translate-y-0.5 transition-transform duration-200 rounded-xl px-8 py-4 font-semibold text-lg disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>

          <button
            onClick={() => navigate('/')}
            className="mt-6 text-[#5C5C66] hover:text-[#0A0A0B] text-sm font-medium w-full text-center"
          >
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#E5E5EB] py-4">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 flex justify-between items-center">
          <h1 className="text-2xl font-black tracking-tighter cursor-pointer" onClick={() => navigate('/')} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Clarity
          </h1>
          <span className="text-sm font-semibold uppercase tracking-wider text-[#5C5C66]">Admin Dashboard</span>
        </div>
      </nav>

      {/* Dashboard Content */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 sm:px-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0B] mb-12">Dashboard Overview</h2>

            {stats ? (
              <div className="space-y-8">
                {/* Top Metrics */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white border border-[#E5E5EB] rounded-2xl p-8">
                    <p className="text-sm font-semibold uppercase tracking-wider text-[#5C5C66] mb-2">Total Plans Generated</p>
                    <p className="text-5xl font-bold text-[#534AB7]">{stats.total_plans}</p>
                  </div>
                  <div className="bg-white border border-[#E5E5EB] rounded-2xl p-8">
                    <p className="text-sm font-semibold uppercase tracking-wider text-[#5C5C66] mb-2">Total Users</p>
                    <p className="text-5xl font-bold text-[#534AB7]">{stats.total_users}</p>
                  </div>
                </div>

                {/* Feedback Results */}
                <div className="bg-[#F6F6F9] border border-[#E5E5EB] rounded-2xl p-8">
                  <h3 className="text-xl sm:text-2xl font-semibold text-[#0A0A0B] mb-6">Feedback Results</h3>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div>
                      <p className="text-base text-[#5C5C66] mb-4 font-semibold">Was this advice useful?</p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center bg-white rounded-xl px-4 py-3 border border-[#E5E5EB]">
                          <span className="text-[#0A0A0B] font-medium">Yes</span>
                          <span className="text-[#534AB7] font-bold text-lg">{stats.feedback_useful_yes}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white rounded-xl px-4 py-3 border border-[#E5E5EB]">
                          <span className="text-[#0A0A0B] font-medium">No</span>
                          <span className="text-[#EF4444] font-bold text-lg">{stats.feedback_useful_no}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-base text-[#5C5C66] mb-4 font-semibold">Would pay RM15/month?</p>
                      <div className="space-y-2">
                        <div className="flex justify-between items-center bg-white rounded-xl px-4 py-3 border border-[#E5E5EB]">
                          <span className="text-[#0A0A0B] font-medium">Yes</span>
                          <span className="text-[#534AB7] font-bold text-lg">{stats.feedback_pay_yes}</span>
                        </div>
                        <div className="flex justify-between items-center bg-white rounded-xl px-4 py-3 border border-[#E5E5EB]">
                          <span className="text-[#0A0A0B] font-medium">No</span>
                          <span className="text-[#EF4444] font-bold text-lg">{stats.feedback_pay_no}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-[#5C5C66]">Loading stats...</p>
            )}
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;